import { useCallback, useEffect, useState } from 'react';
import { Partner, PartnerFilter } from '../../types/partner';
import { APIDataCollection, Metadata } from '../../network/types';
import { getPartners } from '../../network/authApi/methods/Partner';

export const usePartners = (pageChange?: number, searchQuery: string = '', filters?: PartnerFilter | null) => {
  const [partners, setPartners] = useState<Array<Partner>>([]);
  const [partnersMetadata, setPartnersMetadata] = useState<Metadata | undefined>();
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [errorPartners, setErrorPartners] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (pageChange) {
      setPage(pageChange);
    }
  }, [pageChange]);

  const loadMore = () => {
    setPage(page + 1);
  };

  useEffect(() => {
    setPartners([]);
    setPage(1);
  }, [searchQuery, filters]);

  const retrieveUsers = useCallback((query: string, filters?: PartnerFilter | null) => {
    setLoadingPartners(true);
    const params = new URLSearchParams();
    params.append('search', query);
    params.append('pagination', 'false');
    // params.append('page', page.toString());
    params.append('nbResults', '100');
    const fields: Array<keyof Partner> = ['id', 'name', 'isActive', 'numClient', 'isM3'];
    fields.forEach(value => {
      params.append('fields[]', value);
    });

    if (filters?.isM3 !== undefined) {
      params.append('filters[]', `isM3:eq:${filters.isM3}`);
    }

    if (filters?.active !== undefined) {
      params.append('filters[]', `isActive:eq:${filters.active}`);
    }

    getPartners(params.toString())
      .then((response: APIDataCollection) => {
        const result = Object.values(response['hydra:member']);

        setPartnersMetadata({
          count: response['hydra:totalItems'],
          first: response['hydra:view']['hydra:first'],
          last: response['hydra:view']['hydra:first'],
          previous: response['hydra:view']['hydra:previous'],
          next: response['hydra:view']['hydra:next'],
        });
        setPartners(l => [...l, ...result]);
      })
      .catch(error => {
        setErrorPartners(error);
      })
      .finally(() => setLoadingPartners(false));
  }, []);

  useEffect(() => {
    let timeout: any;
    let cancel = false;
    if (!cancel) {
      setLoadingPartners(true);
      if (searchQuery) {
        timeout = setTimeout(() => {
          retrieveUsers(searchQuery, filters);
        }, 2000);
      } else {
        retrieveUsers('', filters);
      }
    }

    return () => {
      clearTimeout(timeout);
      cancel = true;
      setLoadingPartners(false);
    };
  }, [filters, retrieveUsers, searchQuery]);

  return {
    partners,
    loadingPartners,
    errorPartners,
    partnersMetadata,
    loadMore,
  };
};
